import { SagaHttpRequestError, resetCampaignData } from "@api/common";
import { EditCampaignUrl, postCampaignRequest, updateCampaignByIdRequest } from "@common";
import {
    AppState,
    campaignBudgetForm,
    campaignCreativesForm,
    campaignMediaPlanForm,
    changeSaveOrPurchaseProcessingAction,
    createOrUpdateCampaignAction,
    getCampaignByIdAction,
    openNotificationAction,
    PendingCampaign
} from "@store";
import { replace } from "react-router-redux";
import { call, put, select } from "redux-saga/effects";
import { loadCampaignById } from "./loadCampaignById";
import { changeCampaignChangesFlagAction } from "../actions";
import { findCPCCampaignDataSelector } from "../selectors";

export function* createOrUpdateCampaign(action: typeof createOrUpdateCampaignAction.typeInterface) {
    yield put(campaignMediaPlanForm.actions.reset());
    yield put(campaignCreativesForm.actions.reset());
    yield put(campaignBudgetForm.actions.reset());

    const state: AppState = yield select();
    const { mode, campaignId, clientId } = action;

    const mediaPlanForm = campaignMediaPlanForm.selector(state.campaignState.campaignMediaPlanForm);
    const creativesForm = campaignCreativesForm.selector(state.campaignState.campaignCreativesForm);
    const budgetForm = campaignBudgetForm.selector(state.campaignState.campaignBudgetForm);

    if (!mediaPlanForm.isValid || !creativesForm.isValid || !budgetForm.isValid) {
        yield put(campaignMediaPlanForm.actions.validate());
        yield put(campaignCreativesForm.actions.validate());
        yield put(campaignBudgetForm.actions.validate());
        yield put(
            openNotificationAction({
                isOpen: true,
                isWarning: true,
                message: "Please check your selection (red highlights), unable to save campaign in this setting."
            })
        );

        return;
    }

    if (mode === "update") {
        yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: true }));
    }

    const CPCData = findCPCCampaignDataSelector(state);

    const formData: PendingCampaign = {
        ...(CPCData ? CPCData : mediaPlanForm.data),
        ...creativesForm.data,
        ...budgetForm.data,
        clientId: clientId,
        budget: budgetForm.data.budget || 0
    } as any; //TODO: as any should delete

    try {
        yield put(changeCampaignChangesFlagAction({ status: false }));

        if (mode === "create") {
            const response: typeof postCampaignRequest.types.okResult = yield* postCampaignRequest.saga(
                {},
                formData as any
            );

            resetCampaignData();

            yield put(getCampaignByIdAction({ clientId, campaignId: response.campaignId, mode: "edit" }));

            yield put(openNotificationAction({ isOpen: true, message: "Campaign saved!" }));

            yield put(replace(EditCampaignUrl.format({ clientId, campaignId: response.campaignId, mode: "edit" })));
        } else if (mode === "update") {
            yield* updateCampaignByIdRequest.saga({ clientId, campaignId }, formData as any);
            resetCampaignData();

            yield put(getCampaignByIdAction({ clientId, campaignId, mode }));

            yield put(openNotificationAction({ isOpen: true, message: "Campaign saved!" }));

            yield put(replace(EditCampaignUrl.format({ clientId, campaignId, mode: "update" })));

            yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: false }));
        } else if (mode == "purchase new campaign") {
            const response: typeof postCampaignRequest.types.okResult = yield* postCampaignRequest.saga(
                {},
                formData as any
            );
            resetCampaignData();

            yield call(loadCampaignById, { clientId: clientId, campaignId: response.campaignId });

            yield put(openNotificationAction({ isOpen: true, message: "Campaign saved!" }));
        } else {
            yield* updateCampaignByIdRequest.saga({ clientId, campaignId }, formData as any);
            resetCampaignData();

            yield call(loadCampaignById, { clientId: clientId, campaignId: campaignId });

            // yield put(openNotificationAction({ isOpen: true, message: "Campaign saved!" }));
        }
    } catch (e) {
        console.log("Error saving campaign", e);

        if (e.errorResult) {
            const requestError = e as SagaHttpRequestError<any>;
            if (requestError.errorResult.errorType === "response") {
                const error = requestError.errorResult.error as {
                    message: Array<{ property: string }>;
                };

                const formErrors = error.message.reduce((result, err) => {
                    result[err.property.substring("instance.".length)] = ["Value is required."];
                    return result;
                }, {});

                for (const form of [campaignBudgetForm, campaignMediaPlanForm, campaignCreativesForm]) {
                    yield put(
                        (form.actions.setErrors as any)({
                            fields: formErrors
                        } as any)
                    );
                }

                yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: false }));

                yield put(
                    openNotificationAction({
                        isOpen: true,
                        isWarning: true,
                        message:
                            "Please check your selection (red highlights), unable to save campaign in this setting."
                    })
                );

                return;
            }

            if (requestError.errorResult.errorType === "transport" && requestError.errorResult.statusCode === 409) {
                yield put(campaignMediaPlanForm.actions.validate());
                yield put(
                    campaignMediaPlanForm.actions.setErrors({
                        fields: { campaignName: ["Please enter a different name"] }
                    })
                );

                yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: false }));

                yield put(changeCampaignChangesFlagAction({ status: true }));

                yield put(
                    openNotificationAction({
                        isOpen: true,
                        isWarning: true,
                        message: "This name has been already used. Please enter a different name"
                    })
                );

                return;
            }
        }
    }
}
